import React, { useState } from 'react';
import Input from '../../../utils/Input';
import Textarea from '../../../utils/Textarea';
import Button from '../../../utils/Button';
import { useApiMutation } from '../../../hooks/useMutation';
import toast from 'react-hot-toast';
import { US_STATES } from '../../../utils/Constant';

interface ElectionDetails {
  googleElectionId?: string;
  name: string;
  electionDay: string;
  ocdDivisionId: string;
  electionType: 'GENERAL' | 'PRIMARY' | 'SPECIAL';
  stateCode: string;
  electionHours: string;
  registrationDeadline: string;
  mailRequestDeadline: string;
  mailReturnDeadline: string;
  earlyVotingStartDate: string;
  earlyVotingEndDate: string;
  voterIdRequirement: string;
  felonyVotingRules: string;
  electionInfoUrl: string;
  electionRegistrationUrl: string;
  electionRegistrationConfirmationUrl: string;
  absenteeVotingInfoUrl: string;
  votingLocationFinderUrl: string;
  ballotInfoUrl: string;
  electionRulesUrl: string;
  officialStateWebsite: string;
  voterInfoEnriched: boolean;
}

type ViewMode = 'build' | 'preview';

const initialElectionDetails: ElectionDetails = {
  googleElectionId: '',
  name: '',
  electionDay: '',
  ocdDivisionId: '',
  electionType: 'GENERAL',
  stateCode: '',
  electionHours: '',
  registrationDeadline: '',
  mailRequestDeadline: '',
  mailReturnDeadline: '',
  earlyVotingStartDate: '',
  earlyVotingEndDate: '',
  voterIdRequirement: '',
  felonyVotingRules: '',
  electionInfoUrl: '',
  electionRegistrationUrl: '',
  electionRegistrationConfirmationUrl: '',
  absenteeVotingInfoUrl: '',
  votingLocationFinderUrl: '',
  ballotInfoUrl: '',
  electionRulesUrl: '',
  officialStateWebsite: '',
  voterInfoEnriched: true
};

const ElectionInfoTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('build');
  const [electionData, setElectionData] = useState<ElectionDetails>(initialElectionDetails);
  const [savedElections, setSavedElections] = useState<ElectionDetails[]>([]);

  const updateElectionDetails = (field: keyof ElectionDetails, value: string | boolean) => {
    setElectionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveElectionMutation = useApiMutation({
    onSuccess: () => {
      toast.success('Election information saved successfully!');
    },
    onError: (error) => {
      toast.error('Failed to save election information');
      console.error('Save error:', error);
    }
  });

  const handleSave = async () => {
    // Only send if we have the required fields
    if (!electionData.name || !electionData.electionDay || !electionData.stateCode) {
      toast.error('Please fill in the required fields: Name, Election Day, and State Code');
      return;
    }

    // Create request data, only include googleElectionId if it has a value
    const requestData = { ...electionData };
    if (!(requestData.googleElectionId || '').trim()) {
      delete requestData.googleElectionId;
    }

    saveElectionMutation.mutate({
      method: 'POST',
      url: '/elections/admin/elections',
      data: requestData
    });
  };

  const renderElectionForm = () => {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Election Information</h3>
          <Button 
            onClick={handleSave} 
            label="Save Election"
            variant="primary" 
            size="medium"
            disabled={saveElectionMutation.isPending}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Election Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Election Type
            </label>
            <select
              value={electionData.electionType}
              onChange={(e) => updateElectionDetails('electionType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="GENERAL">General Election</option>
              <option value="PRIMARY">Primary Election</option>
              <option value="SPECIAL">Special Election</option>
            </select>
          </div>

          {/* State Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <select
              value={electionData.stateCode}
              onChange={(e) => updateElectionDetails('stateCode', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a state...</option>
              {US_STATES.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          {/* Basic Information */}
          <Input
            label="Google Election ID"
            value={electionData.googleElectionId}
            onChange={(e) => updateElectionDetails('googleElectionId', e.target.value)}
            placeholder="e.g., 2000"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Election Name"
            value={electionData.name}
            onChange={(e) => updateElectionDetails('name', e.target.value)}
            placeholder="e.g., New York General Election"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Election Day"
            type="date"
            value={electionData.electionDay}
            onChange={(e) => updateElectionDetails('electionDay', e.target.value)}
            className="w-full"
            color='white'
          />
          
          <Input
            label="OCD Division ID"
            value={electionData.ocdDivisionId}
            onChange={(e) => updateElectionDetails('ocdDivisionId', e.target.value)}
            placeholder="e.g., ocd-division/country:us/state:ny"
            className="w-full"
            color='white'
          />

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="voterInfoEnriched"
              checked={electionData.voterInfoEnriched}
              onChange={(e) => updateElectionDetails('voterInfoEnriched', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="voterInfoEnriched" className="text-sm font-medium text-gray-700">
              Voter Info Enriched
            </label>
          </div>

          {/* Election Hours and Deadlines */}
          <Input
            label="Election Hours"
            value={electionData.electionHours}
            onChange={(e) => updateElectionDetails('electionHours', e.target.value)}
            placeholder="e.g., 6:00 AM - 9:00 PM"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Registration Deadline"
            type="date"
            value={electionData.registrationDeadline}
            onChange={(e) => updateElectionDetails('registrationDeadline', e.target.value)}
            className="w-full"
            color='white'
          />
          
          <Input
            label="Mail Request Deadline"
            value={electionData.mailRequestDeadline}
            onChange={(e) => updateElectionDetails('mailRequestDeadline', e.target.value)}
            placeholder="e.g., October 15, 2024 or Received by 5:00 PM on October 15, 2024"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Mail Return Deadline"
            value={electionData.mailReturnDeadline}
            onChange={(e) => updateElectionDetails('mailReturnDeadline', e.target.value)}
            placeholder="e.g., November 5, 2024 or Received by 7:00 PM on Election Day"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Early Voting Start Date"
            type="date"
            value={electionData.earlyVotingStartDate}
            onChange={(e) => updateElectionDetails('earlyVotingStartDate', e.target.value)}
            className="w-full"
            color='white'
          />
          
          <Input
            label="Early Voting End Date"
            type="date"
            value={electionData.earlyVotingEndDate}
            onChange={(e) => updateElectionDetails('earlyVotingEndDate', e.target.value)}
            className="w-full"
            color='white'
          />

          {/* Voting Rules */}
          <div className="md:col-span-2">
            <Textarea
              label="Voter ID Requirement"
              value={electionData.voterIdRequirement}
              onChange={(e) => updateElectionDetails('voterIdRequirement', e.target.value)}
              placeholder="e.g., No ID required for most voters. First-time voters may need ID."
              rows={3}
              className="w-full"
              color='white'
            />
          </div>
          
          <div className="md:col-span-2">
            <Textarea
              label="Felony Voting Rules"
              value={electionData.felonyVotingRules}
              onChange={(e) => updateElectionDetails('felonyVotingRules', e.target.value)}
              placeholder="e.g., Voting rights restored after release from prison."
              rows={3}
              className="w-full"
              color='white'
            />
          </div>

          {/* URLs */}
          <div className="md:col-span-2">
            <h4 className="text-md font-semibold text-gray-900 mb-3">Information URLs</h4>
          </div>
          
          <Input
            label="Election Info URL"
            value={electionData.electionInfoUrl}
            onChange={(e) => updateElectionDetails('electionInfoUrl', e.target.value)}
            placeholder="https://elections.ny.gov"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Registration URL"
            value={electionData.electionRegistrationUrl}
            onChange={(e) => updateElectionDetails('electionRegistrationUrl', e.target.value)}
            placeholder="https://elections.ny.gov/register"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Registration Confirmation URL"
            value={electionData.electionRegistrationConfirmationUrl}
            onChange={(e) => updateElectionDetails('electionRegistrationConfirmationUrl', e.target.value)}
            placeholder="https://voterlookup.elections.ny.gov"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Absentee Voting Info URL"
            value={electionData.absenteeVotingInfoUrl}
            onChange={(e) => updateElectionDetails('absenteeVotingInfoUrl', e.target.value)}
            placeholder="https://elections.ny.gov/absentee-voting"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Voting Location Finder URL"
            value={electionData.votingLocationFinderUrl}
            onChange={(e) => updateElectionDetails('votingLocationFinderUrl', e.target.value)}
            placeholder="https://voterlookup.elections.ny.gov"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Ballot Info URL"
            value={electionData.ballotInfoUrl}
            onChange={(e) => updateElectionDetails('ballotInfoUrl', e.target.value)}
            placeholder="https://ballotpedia.org/New_York_elections"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Election Rules URL"
            value={electionData.electionRulesUrl}
            onChange={(e) => updateElectionDetails('electionRulesUrl', e.target.value)}
            placeholder="https://elections.ny.gov/rules"
            type="url"
            className="w-full"
            color='white'
          />
          
          <Input
            label="Official State Website"
            value={electionData.officialStateWebsite}
            onChange={(e) => updateElectionDetails('officialStateWebsite', e.target.value)}
            placeholder="https://dos.ny.gov"
            type="url"
            className="w-full"
            color='white'
          />
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    if (!electionData.name && !electionData.electionDay) {
      return (
        <div className="text-center py-12 text-gray-500">
          <p>No election information has been added yet.</p>
          <p className="text-sm mt-2">Switch to Build mode to add election details.</p>
        </div>
      );
    }

    const getElectionTypeColor = (type: string) => {
      switch (type) {
        case 'PRIMARY': return 'bg-slate-800';
        case 'GENERAL': return 'bg-lime-800';
        case 'SPECIAL': return 'bg-amber-800';
        default: return 'bg-gray-600';
      }
    };

    const getElectionTypeLabel = (type: string) => {
      switch (type) {
        case 'PRIMARY': return 'Primary Election';
        case 'GENERAL': return 'General Election';
        case 'SPECIAL': return 'Special Election';
        default: return 'Election';
      }
    };

    return (
      <div className={`${getElectionTypeColor(electionData.electionType)} rounded-lg p-6 mb-6 text-white`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold">{getElectionTypeLabel(electionData.electionType)}</h3>
          {electionData.electionDay && (
            <div className="bg-white bg-opacity-20 px-4 py-2 rounded-lg">
              <span className="font-semibold">{electionData.electionDay}</span>
            </div>
          )}
        </div>

        {electionData.name && (
          <p className="text-xl mb-4 opacity-95">{electionData.name}</p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {electionData.stateCode && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <h4 className="font-semibold mb-1">State</h4>
              <p className="opacity-90">{electionData.stateCode}</p>
            </div>
          )}

          {electionData.electionHours && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <h4 className="font-semibold mb-1">Election Hours</h4>
              <p className="opacity-90">{electionData.electionHours}</p>
            </div>
          )}

          {electionData.registrationDeadline && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <h4 className="font-semibold mb-1">Registration Deadline</h4>
              <p className="opacity-90">{electionData.registrationDeadline}</p>
            </div>
          )}

          {electionData.earlyVotingStartDate && electionData.earlyVotingEndDate && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <h4 className="font-semibold mb-1">Early Voting</h4>
              <p className="opacity-90">{electionData.earlyVotingStartDate} - {electionData.earlyVotingEndDate}</p>
            </div>
          )}

          {electionData.mailRequestDeadline && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <h4 className="font-semibold mb-1">Mail Request Deadline</h4>
              <p className="opacity-90">{electionData.mailRequestDeadline}</p>
            </div>
          )}

          {electionData.mailReturnDeadline && (
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <h4 className="font-semibold mb-1">Mail Return Deadline</h4>
              <p className="opacity-90">{electionData.mailReturnDeadline}</p>
            </div>
          )}
        </div>

        {electionData.voterIdRequirement && (
          <div className="mt-4 bg-white bg-opacity-10 rounded-lg p-3">
            <h4 className="font-semibold mb-1">Voter ID Requirements</h4>
            <p className="opacity-90 text-sm">{electionData.voterIdRequirement}</p>
          </div>
        )}

        {electionData.felonyVotingRules && (
          <div className="mt-4 bg-white bg-opacity-10 rounded-lg p-3">
            <h4 className="font-semibold mb-1">Felony Voting Rules</h4>
            <p className="opacity-90 text-sm">{electionData.felonyVotingRules}</p>
          </div>
        )}

        {/* Election Information URLs Section */}
        {(electionData.electionInfoUrl || electionData.electionRegistrationUrl || electionData.electionRegistrationConfirmationUrl || 
          electionData.absenteeVotingInfoUrl || electionData.votingLocationFinderUrl || electionData.ballotInfoUrl || 
          electionData.electionRulesUrl || electionData.officialStateWebsite) && (
          <div className="mt-6 bg-white bg-opacity-10 rounded-lg p-4">
            <h4 className="font-semibold mb-3 text-lg">Election Information URLs</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {electionData.electionInfoUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Election Information</h5>
                  <a 
                    href={electionData.electionInfoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.electionInfoUrl}
                  </a>
                </div>
              )}

              {electionData.electionRegistrationUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Voter Registration</h5>
                  <a 
                    href={electionData.electionRegistrationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.electionRegistrationUrl}
                  </a>
                </div>
              )}

              {electionData.electionRegistrationConfirmationUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Registration Confirmation</h5>
                  <a 
                    href={electionData.electionRegistrationConfirmationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.electionRegistrationConfirmationUrl}
                  </a>
                </div>
              )}

              {electionData.absenteeVotingInfoUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Absentee Voting</h5>
                  <a 
                    href={electionData.absenteeVotingInfoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.absenteeVotingInfoUrl}
                  </a>
                </div>
              )}

              {electionData.votingLocationFinderUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Voting Location Finder</h5>
                  <a 
                    href={electionData.votingLocationFinderUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.votingLocationFinderUrl}
                  </a>
                </div>
              )}

              {electionData.ballotInfoUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Ballot Information</h5>
                  <a 
                    href={electionData.ballotInfoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.ballotInfoUrl}
                  </a>
                </div>
              )}

              {electionData.electionRulesUrl && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Election Rules</h5>
                  <a 
                    href={electionData.electionRulesUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.electionRulesUrl}
                  </a>
                </div>
              )}

              {electionData.officialStateWebsite && (
                <div className="bg-white bg-opacity-10 rounded-lg p-3">
                  <h5 className="font-medium mb-2 text-sm opacity-90">Official State Website</h5>
                  <a 
                    href={electionData.officialStateWebsite} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-white hover:underline break-all text-sm"
                  >
                    {electionData.officialStateWebsite}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6 w-full">
        <h2 className="text-2xl font-bold text-gray-900">Election Information</h2>

        <div className="flex items-center justify-end space-x-4">

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('build')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${viewMode === 'build'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Build
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${viewMode === 'preview'
                ? 'bg-slate-900 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Preview
            </button>
          </div>


        </div>
      </div>

      {viewMode === 'build' ? (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">US Election Information</h3>
            <p className="text-blue-700 text-sm">
              Add comprehensive election information for primary, general, and special elections.
              This information will be displayed to voters to help them understand voting requirements, dates, and procedures.
            </p>
          </div>

          {renderElectionForm()}
        </div>
      ) : (
        renderPreview()
      )}
    </div>
  );
};

export default ElectionInfoTab;
